import { Component, effect, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
  KEYCLOAK_EVENT_SIGNAL,
  KeycloakEventType,
  ReadyArgs,
  typeEventArgs,
} from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { CommonModule } from '@angular/common';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { ProfissionalService } from '@app/features-modules/bolsa-de-profissionais/services/';
import { ApiService } from '@core/providers/http';
import { RedefinirSenhaComponent } from '@app/shared/components/redefinir-senha/redefinir-senha.component';
import { MessageService } from 'primeng/api';
import {
  DynamicDialogRef,
  DialogService,
  DynamicDialogConfig,
} from 'primeng/dynamicdialog';
import { ToastModule } from 'primeng/toast';
import { AuthStore } from '@core/service/auth-store.service';
import { AvatarModule } from 'primeng/avatar';
import { getInitials } from '@shared/pipes/initials.pipe';

interface Representante {
  emailRepresentante: string;
  nome: string;
  usernameRepresentante: string;
}

@Component({
  selector: 'app-top-bar',
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.scss'],
  imports: [CommonModule, Menu, ToastModule, AvatarModule],
  providers: [ApiService, ProfissionalService, DialogService,
    MessageService,
    DynamicDialogConfig,
    DynamicDialogRef,],
  standalone: true,
})
export class TopBarComponent {
  private profissionalService = inject(ProfissionalService);
  private messageService = inject(MessageService);
  private dialogService = inject(DialogService);
  private authStore = inject(AuthStore);
  ref: DynamicDialogRef | undefined;
  items: MenuItem[] | undefined;
  authenticated = false;
  keycloakStatus: string | undefined;
  router = inject(Router);
  private readonly keycloak = inject(Keycloak);
  private readonly keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);
  userInfo: any = {};
  imagePreview: string | ArrayBuffer | null = null;
  imageError = false;
  representante: Representante = {
    emailRepresentante: '',
    nome: '',
    usernameRepresentante: '',
  };

  constructor() {
    effect(() => {
      const keycloakEvent = this.keycloakSignal();

      this.keycloakStatus = keycloakEvent.type;

      if (keycloakEvent.type === KeycloakEventType.Ready) {
        this.authenticated = typeEventArgs<ReadyArgs>(keycloakEvent.args);
        if (this.authenticated) {
          this.loadUserData();
        }
      }

      if (keycloakEvent.type === KeycloakEventType.AuthLogout) {
        this.authenticated = false;
      }
    });
    this.setMenuItems();
  }

  loadUserData() {
    this.keycloak
      .loadUserInfo()
      .then((userInfo: any) => {
        this.setUserInfo(userInfo);
      })
      .catch(() => {
        const userInfo = this.authStore.getSessionTokenParsed();
        if (userInfo) this.setUserInfo(userInfo);
      });
  }

  private setUserInfo(userInfo: any) {
    this.userInfo = userInfo;
    this.getPerfil(userInfo);
    this.representante.emailRepresentante = userInfo.email;
    this.representante.nome = userInfo.name;
    this.representante.usernameRepresentante = userInfo.preferred_username;
  }

  getPerfil(userInfo: any) {
    if (!userInfo?.sub) return;
    this.profissionalService.showByUserId(userInfo.sub).subscribe({
      next: (result) => {
        if (result?.retorno?.codigo === 404 || result?.data == null) {
          return;
        }
        if (result?.data?.id) {
          this.getPhoto(result.data.id as any);
        }
      },
      error: (err) => {
        // Se o usuário logado não tiver perfil de profissional na base, não emite erro fatal
        console.debug('Perfil de profissional não localizado para o utilizador:', err?.message || err);
      }
    });
  }

  getPhoto(id: number) {
    this.profissionalService.getPhoto(String(id)).subscribe({
      next: (result) => {
        if (!result || !(result instanceof Blob) || !result.type.startsWith('image/')) {
          this.imagePreview = null;
          return;
        }
        const reader = new FileReader();
        reader.onload = () => {
          this.imagePreview = reader.result;
        };
        reader.readAsDataURL(result);
      },
      error: () => {
        this.imagePreview = null;
      }
    });
  }

  get username() {
    if (!this.userInfo.name) {
      return '';
    }
    let fullNameArray = this.userInfo.name?.split(' ');
    return fullNameArray[0] + ' ' + fullNameArray[fullNameArray.length - 1];
  }

  get iniciais() {
    return getInitials(this.userInfo?.name || this.username);
  }

  login() {
    this.keycloak.login();
  }

  logout() {
    this.keycloak.logout();
  }

  redefinirSenhaRepresentante(representante: any) {
    const ref = this.dialogService.open(RedefinirSenhaComponent, {
      header: 'Redefinir Senha do Representante',
      width: '45%',
      showHeader: false,
      modal: true,
      data: {
        representante,
      },
    });

    ref.onClose.subscribe((data) => {
      if (data) {
        this.messageService.add({
          severity: 'success',
          summary: 'Senha redefinida',
          detail: `Senha redefinida para o usuário ${data.username}.`,
        });
      }
    });
  }

  setMenuItems() {
    this.items = [
      {
        label: 'Redefinir Senha',
        icon: 'pi pi-key',
        command: () => this.redefinirSenhaRepresentante(this.representante),
      },
      {
        label: 'Sair',
        icon: 'pi pi-fw pi-sign-out',
        command: () => this.logout(),
      },
    ];
  }
}
